import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { AlertOctagon, AlertTriangle, Info, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const SEV = {
  error: { icon: AlertOctagon, wrap: "bg-red-50 border-red-200 text-red-900", badge: "bg-red-600 text-white", title: "Şüpheli / anlaşılmayan veri — kontrol edin" },
  warn: { icon: AlertTriangle, wrap: "bg-amber-50 border-amber-200 text-amber-900", badge: "bg-amber-500 text-white", title: "Eksik alanlar — uydurulmaz, boş bırakılır" },
  info: { icon: Info, wrap: "bg-slate-50 border-slate-200 text-slate-700", badge: "bg-slate-500 text-white", title: "Bilgi — yorumlanan / aktarılmayan veriler" },
};

function Group({ g }) {
  const [open, setOpen] = useState(g.sev === "error");
  const shown = open ? g.rows : [];
  return (
    <li className="text-[11px]" data-testid={`import-issue-${g.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-start gap-1 text-left hover:underline" data-testid={`import-issue-toggle-${g.key}`}>
        {open ? <ChevronDown className="h-3 w-3 mt-0.5 shrink-0" /> : <ChevronRight className="h-3 w-3 mt-0.5 shrink-0" />}
        <span>{g.text}</span>
      </button>
      {shown.length > 0 && (
        <ul className="ml-4 mt-0.5 space-y-px" data-testid={`import-issue-rows-${g.key}`}>
          {shown.slice(0, 12).map((r, i) => (
            <li key={i} className="font-mono text-[10.5px] opacity-90">{r.excelRow ? `Satır ${r.excelRow}: ` : ""}<b className="font-sans">{r.label}</b>{r.detail ? ` — ${r.detail}` : ""}</li>
          ))}
          {shown.length > 12 && <li className="text-[10.5px] italic">… ve {shown.length - 12} satır daha</li>}
        </ul>
      )}
    </li>
  );
}

/** Önizleme uyarı paneli: error / warn / info grupları + boş tarih doldurma anahtarı. */
export default function ImportIssues({ issues, fillOn, onFill }) {
  if (!issues.length) return null;
  const bySev = ["error", "warn", "info"].map((s) => [s, issues.filter((x) => x.sev === s)]).filter(([, l]) => l.length);
  const hasDate = issues.some((x) => x.key === "date");
  return (
    <div className="border-b divide-y" data-testid="import-issues">
      {bySev.map(([sev, list]) => {
        const S = SEV[sev];
        return (
          <div key={sev} className={cn("px-4 py-2 border-l-4", S.wrap, sev === "error" ? "border-l-red-500" : sev === "warn" ? "border-l-amber-400" : "border-l-slate-300")} data-testid={`import-issues-${sev}`}>
            <div className="flex items-start gap-2">
              <S.icon className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold flex items-center gap-2">{S.title}<span className={cn("rounded-full px-1.5 py-px text-[10px] font-bold", S.badge)}>{list.reduce((s, g) => s + g.rows.length, 0)}</span></div>
                <ul className="mt-0.5 space-y-0.5">{list.map((g) => <Group key={g.key} g={g} />)}</ul>
              </div>
              {sev === "warn" && hasDate && (
                <label className="flex items-center gap-2 text-[11px] font-semibold shrink-0 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 cursor-pointer self-start" title="Kapalı: tarihler boş kalır (önerilen). Açık: boş tarih önceki satırın tarihiyle doldurulur." data-testid="import-filldown-row">
                  <Switch checked={fillOn} onCheckedChange={onFill} data-testid="import-filldown" />
                  Boş tarihleri önceki satırdan doldur
                </label>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
